import React, { useState } from 'react';
import { InvoicePreview } from './invoice/invoice-preview.component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useRedux';
import { useSelector } from 'react-redux';
import SendInvoiceConfirmationModal from './send-invoice-confirmation-modal.component';
import { formatAmountWithLocale } from '../helper/common.helper';
import { LatestDocumentStatus } from '../features/bankid/bankid.slice';
import { ARTICLE_NUMBER_TO_DEDUCTION_TYPE, TAX_FREE_ARTICLE_CONFIG } from '../constants/invoice-deductions.constants';
import { isInternalCollectionFeeAccount } from '../constants/invoice-account.constants';
import apiCall from '../../api';
import BankInfoModal from './bank-info-modal.component';
import CompleteProfileModal from './modal/onboarding/complete-profile-modal.component';
import './invoice-preview-modal.scss';

interface InvoicePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  brandName?: string;
  logoUrl?: string;
  invoiceNumber?: string;
  totalAmount?: string;
  calculatedSubAmount?: any;
  isEditMode?: boolean;
  onSendInvoice?: () => void;
  isLoading?: boolean;
  invoiceDay?: string;
  dueDay?: string;
  deductionsByFreelancer?: any;
  copiedInvoiceData?: any;
  onBankIdVerificationRequired?: () => void;
  dataRef?: React.MutableRefObject<any>;
  validateServiceDescriptionsRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  validateDatesRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  freelancerSubtotals?: Array<{ name: string; subtotal: string }>;
}

export const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
  isOpen,
  onClose,
  brandName,
  logoUrl,
  invoiceNumber = 'INV_25082025_001',
  totalAmount = 'XXXXX,XX SEK',
  calculatedSubAmount,
  isEditMode,
  onSendInvoice,
  isLoading = false,
  invoiceDay = '',
  dueDay = '',
  deductionsByFreelancer = {},
  copiedInvoiceData,
  onBankIdVerificationRequired,
  dataRef,
  validateServiceDescriptionsRef,
  validateDatesRef,
  freelancerSubtotals = [],
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { preliminaryInvoice, preliminaryLoading } = useAppSelector((state: any) => state?.invoice);
  const isIdentityVerified = useSelector((state: any) => state?.bankidAuthFlow?.isIdentityVerified);
  const latestDocumentStatus = useSelector((state: any) => state?.bankidAuthFlow?.latestDocumentStatus);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBankInfoModal, setShowBankInfoModal] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [initialProfileCountry, setInitialProfileCountry] = useState('');

  // Extract query parameters for warning condition
  const queryParams = new URLSearchParams(window.location.search);
  const customerIdFromQuery = parseInt(queryParams.get("customerId") || "", 10) || undefined;
  const preId = queryParams.get("preId") || undefined;
  
  const checkBankInfo = async (): Promise<boolean> => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        toast.error(String(t('User data not found') || 'User data not found'));
        return false;
      }
      const user = JSON.parse(userDataString);
      const userId = user?.userData?.userId;
      if (!userId) {
        toast.error(String(t('User ID not found') || 'User ID not found'));
        return false;
      }
      const res = await apiCall.get(`/employee/get/${userId}`);
      const response = res?.data?.response;
      if (response?.customerSalaryInformation?.length === 0) {
        setShowBankInfoModal(true);
        return false;
      }
      if (
        !response?.customerSalaryInformation[0]?.accountNumber &&
        !response?.customerSalaryInformation[0]?.clearingNumber &&
        !response?.customerSalaryInformation[0]?.ibanNumber &&
        !response?.customerSalaryInformation[0]?.bicNumber
      ) {
        setShowBankInfoModal(true);
        return false;
      }
      const hasDomesticBankInfo =
        response?.customerSalaryInformation[0]?.accountNumber &&
        response?.customerSalaryInformation[0]?.clearingNumber;
      const hasInternationalBankInfo =
        response?.customerSalaryInformation[0]?.ibanNumber &&
        response?.customerSalaryInformation[0]?.bicNumber;
      if (!hasDomesticBankInfo && !hasInternationalBankInfo) {
        setShowBankInfoModal(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Error checking bank info:', err);
      toast.error(String(t('Error checking bank information') || 'Error checking bank information'));
      return false;
    }
  };

  const checkCompleteProfile = async (): Promise<boolean> => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) return true;
      const user = JSON.parse(userDataString);
      const userId = user?.userData?.userId;
      if (!userId) return true;
      const res = await apiCall.get(`/signupuserv2/user/${userId}`);
      const userProfile = res?.data?.response?.user;
      if (!userProfile?.city || !userProfile?.country) {
        setInitialProfileCountry(userProfile?.country || '');
        setShowCompleteProfileModal(true);
        return false;
      }
      return true;
    } catch (err) {
      return true;
    }
  };

  const validateDescription = (): boolean => {
    const invoiceData = dataRef?.current;
    const description = invoiceData?.formData?.otherDetails?.customerNotes || '';
    if (!description || description.trim().length === 0) {
      toast.error(String(t('PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE') || 'Please enter customer notes (description) before creating invoice'));
      return false;
    }
    const descriptionLength = description.trim().length;
    const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    if (descriptionLength < 20 && wordCount < 3) {
      toast.error(String(t('Enter at least 20 characters or 3 words') || 'Enter at least 20 characters or 3 words in customer notes'));
      return false;
    }
    return true;
  };

  const handleSendInvoiceClick = async () => {
    // 0. Check identity verification (skip in edit mode)
    if (!isEditMode && isIdentityVerified === false && latestDocumentStatus !== LatestDocumentStatus.Approved) {
      if (onBankIdVerificationRequired) onBankIdVerificationRequired();
      return;
    }

    setIsValidating(true);

    // 1. Validate working hours: no article row must have amount = 0 or amountPerUnit = 0
    const invoiceDataEarly = dataRef?.current;
    const ALL_VALIDATED_ARTICLE_NUMBERS = new Set([
      '92', '94', '95', '96', '97',
      ...Object.keys(ARTICLE_NUMBER_TO_DEDUCTION_TYPE),
      ...Object.values(TAX_FREE_ARTICLE_CONFIG).map((c) => c.articleNumber),
    ]);
    const serviceTableDataByFreelancer = invoiceDataEarly?.serviceTableDataByFreelancer || {};

    // Require at least ONE working-hour line (only-deduction invoices aren't valid).
    const totalWorkingHours = Object.values(serviceTableDataByFreelancer).reduce(
      (n: number, fd: any) => n + (Array.isArray(fd?.workingHours) ? fd.workingHours.length : 0),
      0
    );
    if (totalWorkingHours === 0) {
      toast.error(String(t('PLEASE_ADD_AT_LEAST_ONE_WORKING_HOUR') || 'Please add at least one working hour before creating the invoice.'));
      setIsValidating(false);
      return;
    }

    const hasZeroAmountRow = Object.values(serviceTableDataByFreelancer).some((freelancerData: any) => {
      const workingHours = freelancerData?.workingHours || [];
      return workingHours.some((wh: any) => {
        if (!ALL_VALIDATED_ARTICLE_NUMBERS.has(String(wh.articleNumber))) return false;
        return Number(wh.amount) === 0 || Number(wh.amountPerUnit) === 0;
      });
    });
    if (hasZeroAmountRow) {
      toast.error(String(t('WORKING_HOUR_AMOUNT_ZERO_ERROR') || 'One or more working hours have amount 0. Please enter a valid price before creating the invoice.'));
      setIsValidating(false);
      return;
    }

    // 2. Check if profile (city/country) is complete
    const isProfileComplete = await checkCompleteProfile();
    if (!isProfileComplete) {
      setIsValidating(false);
      return;
    }

    // 3. Check bank info
    const hasBankInfo = await checkBankInfo();
    if (!hasBankInfo) {
      setIsValidating(false);
      return;
    }

    // 4. Validate ROT deduction requirements
    const invoiceData = dataRef?.current;
    const preferences = invoiceData?.preferences || {};
    if (preferences.deductionType === 'rot') {
      const accommodationType = preferences.accommodationType;
      if (accommodationType === 'apartment') {
        if (!preferences.brfOrgNumber || !preferences.brfOrgNumber.trim()) {
          toast.error(String(t('ROT_REQUIRES_BRF_ORG_NUMBER')));
          setIsValidating(false);
          return;
        }
        if (!preferences.apartmentHouseNo || !preferences.apartmentHouseNo.trim()) {
          toast.error(String(t('ROT_REQUIRES_APARTMENT_HOUSE_NO')));
          setIsValidating(false);
          return;
        }
      } else if (accommodationType === 'house') {
        if (!preferences.property || !preferences.property.trim()) {
          toast.error(String(t('ROT_REQUIRES_PROPERTY')));
          setIsValidating(false);
          return;
        }
      }
    }

    // 5. Validate minimum invoice amount (1000 SEK) — internal collection-fee account is exempt
    const userDataStringForMinAmount = localStorage.getItem('user');
    const userForMinAmount = userDataStringForMinAmount ? JSON.parse(userDataStringForMinAmount) : null;
    const isExemptFromMinimumAmount = isInternalCollectionFeeAccount(userForMinAmount?.userData?.userId);

    if (!isExemptFromMinimumAmount) {
      const totalCalculation = calculatedSubAmount?.response?.totalCalculation;
      const currencyName = totalCalculation?.currencyName;
      const amount = Number(totalCalculation?.amount || 0);
      const amountInSek = Number(
        totalCalculation?.amountInSek ?? totalCalculation?.amountInSEK ?? amount
      );
      const amountToValidateInSek = currencyName === 'SEK' ? amount : amountInSek;
      if (amountToValidateInSek < 1000) {
        toast.error(String(t('MINIMUM_INVOICE_AMOUNT_ERROR') || 'Minimum 1000 SEK amount is required to create an invoice'));
        setIsValidating(false);
        return;
      }
    }

    // 6. Validate description
    if (!validateDescription()) {
      setIsValidating(false);
      return;
    }

    // Validate ROT/RUT requires at least one Payment Per Hour (article 92)
    const prefs = invoiceData?.preferences || {};
    if (prefs.deductionType === 'rot' || prefs.deductionType === 'rut') {
      const hasHourlyRow = Object.values(serviceTableDataByFreelancer).some((freelancerData: any) => {
        const workingHours = freelancerData?.workingHours || [];
        return workingHours.some((wh: any) => String(wh.articleNumber) === '92');
      });
      if (!hasHourlyRow) {
        toast.error(String(t('ROT_RUT_REQUIRES_PAYMENT_PER_HOUR') || 'When using ROT/RUT deduction, at least one working hour must be of type Payment Per Hour.'));
        return;
      }
    }


    // 7. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t('PLEASE_FILL_SERVICE_DESCRIPTION') || 'Please fill in service description for all working hours'));
          setIsValidating(false);
          return;
        }
      }
    }

    // 8. Validate dates: every working hour row must have a date
    if (validateDatesRef?.current) {
      const dateFunctions = validateDatesRef.current;
      const freelancerIds = Object.keys(dateFunctions);
      for (const freelancerId of freelancerIds) {
        const validateFunc = dateFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t('DATE_REQUIRED_ERROR') || 'Date is required for all rows'));
          setIsValidating(false);
          return;
        }
      }
    }

    // 9. All validations passed — show confirmation modal
    setIsValidating(false);
    setShowConfirmModal(true);
  };
  
  const handleConfirmSend = async () => {
    if (onSendInvoice) {
      const result = await onSendInvoice();
      // Return full result (could be boolean or API response object)
      // This allows the modal to extract isAlreadySynced if available
      return result;
    }
    return false;
  };
  
  const handleSuccess = () => {
    setShowConfirmModal(false);
    // Bypass unsaved-changes guard since invoice was just sent
    window.dispatchEvent(new CustomEvent('invoice-sent-successfully'));
    // Navigate to manage invoice page with current language
    const currentLanguage = i18n.language || 'en-us';
    navigate(`/v2/manage-invoice?lang=${currentLanguage}`);
  };
  
  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmModal(false);
    }
  };

  // Extract PDF data from response (matching old implementation pattern)
  // Reference: usePreliminaryInvoice.js
//   if (preliminaryResponse instanceof Blob) {
//         // The response is a blob from FileContentResult
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data instanceof Blob) {
//         // If response.data is a blob
//         pdfData = preliminaryResponse.data;
//       } else if (typeof preliminaryResponse === 'string') {
//         // Backwards compatibility for direct base64 response
//         pdfData = preliminaryResponse;
//       } else if (preliminaryResponse?.data && typeof preliminaryResponse.data === 'string') {
//         // Backwards compatibility for base64 strings in response.data
//         pdfData = preliminaryResponse.data;
//       } else if (preliminaryResponse?.pdfData) {
//         // Alternative property name
//         pdfData = preliminaryResponse.pdfData;
//       }
      
//       if (pdfData) {
//         setPdfData(pdfData);
//         return pdfData;
//       } else {
//         setError('No PDF data received from server');
//         return null;
//       }
 
  // preliminaryInvoice is now a string (object URL) from Redux
  const pdfUrl = preliminaryInvoice && typeof preliminaryInvoice === 'string' 
    ? preliminaryInvoice 
    : null;

  if (!isOpen) return null;

  return (
    <div className="invoice-preview-page">
      <div className="invoice-preview-modal__container">
        {/* Left Sidebar */}
        <div className='row'>
          <div className='col-xl-4 col-lg-4 col-md-12 col-sm-12 p-0'>
        <div className="invoice-preview-modal__sidebar">
          <div className="sidebar-header">
            <h2 className="sidebar-title">{t('PREVIEW_INVOICE')}</h2>
            <div className="sidebar-amount">
              {calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat 
                ? `${calculatedSubAmount.response.totalCalculation.amountIncludingVat} ${calculatedSubAmount.response.totalCalculation.currencyName || 'SEK'}`
                : totalAmount
              }
            </div>
            {(() => {
              const calc = calculatedSubAmount?.response?.totalCalculation;
              if (!calc) return null;
              const rutAmt = calc.rutDeductionAmount || 0;
              const rotAmt = calc.rootDeductionAmount || 0;
              if (rutAmt <= 0 && rotAmt <= 0) return null;
              const customerPays = calc.amountIncludingVatAfterHouseholdServiceDeduction || 0;
              return (
                <div className="sidebar-deduction-breakdown">
                  <div className="deduction-row deduction-row--highlight">
                    <span className="deduction-label">{t('CUSTOMER_WILL_PAY') || 'Customer will pay'}</span>
                    <span className="deduction-value deduction-value--primary">{formatAmountWithLocale(customerPays, 'SEK', i18n.language)}</span>
                  </div>
                  {rutAmt > 0 && (
                    <div className="deduction-row">
                      <div className="deduction-badge deduction-badge--rut">RUT</div>
                      <span className="deduction-label">{t('RUT_DEDUCTION_AMOUNT') || 'RUT Deduction'}</span>
                      <span className="deduction-value deduction-value--deduct">-{formatAmountWithLocale(rutAmt, 'SEK', i18n.language)}</span>
                    </div>
                  )}
                  {rotAmt > 0 && (
                    <div className="deduction-row">
                      <div className="deduction-badge deduction-badge--rot">ROT</div>
                      <span className="deduction-label">{t('ROT_DEDUCTION_AMOUNT') || 'ROT Deduction'}</span>
                      <span className="deduction-value deduction-value--deduct">-{formatAmountWithLocale(rotAmt, 'SEK', i18n.language)}</span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="sidebar-status">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z" fill="#ede4ff" stroke="#7c3aed" strokeWidth="1.5"/>
              <circle cx="8" cy="8" r="3" fill="#7c3aed"/>
            </svg>
            <span>{t('IN_DRAFT')}</span>
          </div>

          <div className="sidebar-actions">
            {/* <button className="action-btn download-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5" stroke="#544196" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('DOWNLOAD')}
            </button>

            <button className="action-btn print-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5 7.5V2.5H15V7.5M5 15H3.33333C2.89131 15 2.46738 14.8244 2.15482 14.5118C1.84226 14.1993 1.66667 13.7754 1.66667 13.3333V10C1.66667 9.55797 1.84226 9.13405 2.15482 8.82149C2.46738 8.50893 2.89131 8.33333 3.33333 8.33333H16.6667C17.1087 8.33333 17.5326 8.50893 17.8452 8.82149C18.1577 9.13405 18.3333 9.55797 18.3333 10V13.3333C18.3333 13.7754 18.1577 14.1993 17.8452 14.5118C17.5326 14.8244 17.1087 15 16.6667 15H15M5 12.5H15V17.5H5V12.5Z" stroke="#544196" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('PRINT')}
            </button> */}

            <button className="action-btn send-btn" onClick={handleSendInvoiceClick} disabled={isLoading || isValidating} style={{ opacity: (isLoading || isValidating) ? 0.7 : 1, cursor: (isLoading || isValidating) ? 'not-allowed' : 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M18.3333 2.5L9.16667 11.6667M18.3333 2.5L12.5 18.3333L9.16667 11.6667M18.3333 2.5L1.66667 7.5L9.16667 11.6667" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {(isLoading || isValidating) ? t('SENDING') : (isEditMode ? t('EDIT_REQUEST_INVOICE') : t('SEND_INVOICE'))}
            </button>
          </div>

          {/* <div className="sidebar-email-section"> */}
            {/* <h3 className="section-title">Email will be sent to :</h3>
            <div className="email-input-group">
              <input type="email" placeholder="Enter email id" className="email-input" />
              <button className="add-email-btn">Add</button>
            </div> */}
            {/* <div className="email-list">
              <div className="email-item">
                <span>jainankit9340@gmail.com</span>
                <button className="remove-email">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div className="email-item">
                <span>hiHanna@gmail.com</span>
                <button className="remove-email">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div> */}
          {/* </div> */}

          {(() => {
            // Extract all files from deductions, copiedInvoiceData.file, and workingHours
            const allFiles: Array<{ name: string; file: File | Blob; deductionType: string }> = [];
             
            // 1. Extract files from deductionsByFreelancer
            Object.keys(deductionsByFreelancer).forEach((freelancerId) => {
              const deductions = deductionsByFreelancer[freelancerId] || [];
              
              deductions.forEach((deduction: any, idx: number) => {
               
                if (deduction.files && deduction.files.length > 0) {
                  deduction.files.forEach((file: File) => {
                    allFiles.push({
                      name: file.name,
                      file: file,
                      deductionType: deduction.deductionType || 'document'
                    });
                  });
                }
              });
            });

            // 2. Extract files from copiedInvoiceData.file[0].fileContent
            if (copiedInvoiceData?.file && Array.isArray(copiedInvoiceData.file)) {
              copiedInvoiceData.file.forEach((fileItem: any, idx: number) => {
                if (fileItem.fileContent) {
                  try {
                    // Convert base64 or raw content to Blob
                    const blob = new Blob([fileItem.fileContent], { type: 'application/octet-stream' });
                    allFiles.push({
                      name: fileItem.fileName || `file_${idx + 1}`,
                      file: blob,
                      deductionType: 'invoice_file'
                    });
                  } catch (error) {
                    console.error('Error processing copiedInvoiceData file:', error);
                  }
                }
              });
            }

            // 3. Extract files from copiedInvoiceData.workingHours[index].fileContent
            if (copiedInvoiceData?.workingHours && Array.isArray(copiedInvoiceData.workingHours)) {
              copiedInvoiceData.workingHours.forEach((workingHour: any, idx: number) => {
                if (workingHour.fileContent) {
                  try {
                    // Convert base64 or raw content to Blob
                    const blob = new Blob([workingHour.fileContent], { type: 'application/octet-stream' });
                    allFiles.push({
                      name: workingHour.fileName || `working_hour_${idx + 1}`,
                      file: blob,
                      deductionType: 'working_hour_file'
                    });
                  } catch (error) {
                    console.error('Error processing workingHours file:', error);
                  }
                }
              });
            }
            
           
            if (allFiles.length === 0) return null;
            
            return (
              <div className="sidebar-attachments">
                <h3 className="section-title">{t('ADDED_ATTACHMENT')}</h3>
                <div className="attachment-list">
                  {allFiles.map((fileItem, index) => (
                    <div 
                      key={index} 
                      className="attachment-item"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        // Create object URL and open in new tab
                        const fileUrl = URL.createObjectURL(fileItem.file);
                        window.open(fileUrl, '_blank');
                      }}
                      title={t('CLICK_TO_VIEW_ATTACHMENT')}
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M11.6667 1.66667H5C4.55797 1.66667 4.13405 1.84226 3.82149 2.15482C3.50893 2.46738 3.33333 2.89131 3.33333 3.33333V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="11.6667,1.66667 11.6667,6.66667 16.6667,6.66667" stroke="#E33B32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{fileItem.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div></div>

<div className='col-xl-8 col-lg-8 col-md-12 col-sm-12 p-0'>
        {/* Right Content Area */}
        <div className="invoice-preview-modal__content">
          <button className="invoice-preview-modal__close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#252525" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div className="invoice-preview-modal__header">
            <h1 className="preview-title">{t('PRELIMINARY_INVOICE_PREVIEW')}</h1>
          </div>

          <div className="invoice-preview-modal__preview-wrapper">
            {preliminaryLoading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '700px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">{t('LOADING')}</span>
                </div>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl ?? undefined}
                width="100%"
                height="700px"
                style={{ border: 'none' }}
                title={t('PRELIMINARY_INVOICE_PDF')}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '700px' }}>
                <p className="text-muted">{t('NO_PDF_DATA_AVAILABLE')}</p>
              </div>
            )}
          </div>
        </div>
        </div>
        </div>
      </div>
      
      <SendInvoiceConfirmationModal
        show={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSend}
        onSuccess={handleSuccess}
        isLoading={isLoading}
        isEditMode={isEditMode}
        invoiceAmount={totalAmount}
        invoiceDay={invoiceDay}
        dueDay={dueDay}
        calculatedSubAmount={calculatedSubAmount}
        customerIdFromQuery={customerIdFromQuery}
        preId={preId}
        dueDateFromEdit={dueDay}
      />

      <BankInfoModal
        show={showBankInfoModal}
        onClose={() => setShowBankInfoModal(false)}
      />
      <CompleteProfileModal
        show={showCompleteProfileModal}
        onClose={() => setShowCompleteProfileModal(false)}
        onStartTour={() => setShowCompleteProfileModal(false)}
        isFromInvoice={true}
        initialCountry={initialProfileCountry}
      />
    </div>
  );
};

export default InvoicePreviewModal;
